import { BalanceData } from "../data/balance";
import { BossTierData, EventModesData, MEDALS, Medal, OdysseyWeekData, RaceRoadData } from "../data/eventModes";
import { EventsData, TrialModifierData } from "../data/events";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { EVENT_RUN_MODES, EventRunMode, EventRunRule } from "../sim/eventRun";
import { LevelModifiers } from "../sim/modifiers";
import { rotationIndex } from "./eventSchedule";
import { utcWeek } from "./orders";

/**
 * The weekly event modes as rules (MP-21, docs/MODES.md §5): Boss Week's tiers, Race Week's road and medals, the
 * Odyssey's maps. Each week's content comes from the data in turn by the kind's own week count (as Hunters' quarry
 * does), so it is the same for every player and needs no server. The raiders' power is the player's: a run's health is
 * anchored at the player's highest cleared level (locked for the week at the first look: meta/eventModesState.ts),
 * whatever layout it borrows, then raised by the tier. Pure: the sheet and the run build the same thing.
 */

/** Hash tag of Race Week's seed, apart from every other hash of a week. */
const RACE_SEED_TAG = "raceWeek";

/** The mode of a rotation week (its kind's id), or null for any other event. */
export function eventModeOf(e: { readonly kind: string } | null): EventRunMode | null {
  return e !== null && (EVENT_RUN_MODES as readonly string[]).includes(e.kind) ? (e.kind as EventRunMode) : null;
}

/** How many weeks of `kind` the rotation ran before the week starting on Monday `monday` (0 = its first). */
export function kindRound(data: EventsData, monday: number): number {
  const n = data.rotation?.kinds.length ?? 0, index = rotationIndex(data, monday);
  return n === 0 || index < 0 ? 0 : Math.floor(index / n);
}

const pick = <T>(list: readonly T[], round: number): T => list[((round % list.length) + list.length) % list.length];

/** The raiders' health on `layout` anchored at level `anchor`'s power: levelBase(anchor) / levelBase(layout). */
export function anchorHpMult(balance: BalanceData, layout: number, anchor: number): Fix64 {
  const t = balance.tables, a = Math.max(1, Math.min(anchor, t.maxLevel));
  return t.levelBaseAt(a).div(t.levelBaseAt(Math.max(1, Math.min(layout, t.maxLevel))));
}

function modesOf(data: EventsData): EventModesData {
  if (!data.modes) throw new Error("events.modes: no event-mode data in this bundle");
  return data.modes;
}

function rule(mode: EventRunMode, week: number, step: number, stages: number, extra: Partial<EventRunRule> = {}): EventRunRule {
  return { mode, week, step, stages, countMult: Fix64.One, armour: Fix64.Zero, towers: [], lives: 0, ...extra };
}

// ------------------------------------------------------------------ Boss Week

/** One tier's fight: the layout, its modifiers, the tier's data and (Elite) the Weekly Trial modifier on top. */
export interface BossFight {
  readonly week: number;
  readonly tier: number;
  readonly level: number;
  readonly data: BossTierData;
  readonly twist: TrialModifierData | null;
  readonly modifiers: LevelModifiers;
}

/** The week's layout: data.boss.layouts in turn by Boss Week's own week count. */
export function bossLayout(data: EventsData, monday: number): number { return pick(modesOf(data).boss.layouts, kindRound(data, monday)); }

/** The Weekly Trial's modifier of the week starting on `monday` (the same one the Trial plays). */
export function weekTwist(data: EventsData, monday: number): TrialModifierData | null {
  const list = data.trial.modifiers;
  return list.length === 0 ? null : pick(list, utcWeek(monday));
}

/**
 * Tier `tier` (1-based; the last is Elite) of the week starting on `monday` at the player's `anchor`: the same fight
 * each tier, health × the tier's hpMult (× the twist's on Elite), armour added, counts raised; Elite also takes the
 * twist's elites in place of the layout's.
 */
export function bossFight(balance: BalanceData, monday: number, tier: number, anchor: number): BossFight {
  const d = modesOf(balance.events).boss, t = d.tiers[tier - 1];
  if (!t) throw new RangeError(`Boss Week has no tier ${tier}`);
  const level = Math.min(bossLayout(balance.events, monday), balance.levels.length);
  const twist = t.elite ? weekTwist(balance.events, monday) : null;
  let hp = anchorHpMult(balance, level, anchor).mul(d.hpBase).mul(t.hpMult);
  if (twist) hp = hp.mul(twist.enemyHpMult);
  const event = rule("boss", monday, tier, d.stages, { countMult: t.countMult, armour: t.armour });
  const modifiers: LevelModifiers = twist && twist.elites.length > 0 ? { enemyHpMult: hp, elites: twist.elites, event } : { enemyHpMult: hp, event };
  return { week: monday, tier, level, data: t, twist, modifiers };
}

/** Tier `tier` opens once the tier before it is cleared (tier 1 always; Elite after the last plain tier). */
export function bossTierOpen(cleared: readonly number[], tier: number): boolean { return tier <= 1 || cleared.includes(tier - 1); }

// ------------------------------------------------------------------ Race Week

export interface RaceRoad {
  readonly week: number;
  readonly level: number;
  readonly road: RaceRoadData;
  readonly seed: bigint;
  readonly modifiers: LevelModifiers;
}

/** The week's road: data.race.roads in turn by Race Week's own week count. */
export function raceRoadData(data: EventsData, monday: number): RaceRoadData { return pick(modesOf(data).race.roads, kindRound(data, monday)); }

/** The week's seed: the card offers and every roll of the run, the same for every player and every try. */
export function raceSeed(monday: number): bigint {
  const h = new Fnv1a64();
  h.writeString(RACE_SEED_TAG);
  h.writeInt(monday);
  return h.value;
}

/** The road of the week starting on `monday` at the player's `anchor`. */
export function raceRoad(balance: BalanceData, monday: number, anchor: number): RaceRoad {
  const road = raceRoadData(balance.events, monday), level = Math.min(road.level, balance.levels.length);
  return { week: monday, level, road, seed: raceSeed(monday), modifiers: { enemyHpMult: anchorHpMult(balance, level, anchor), event: rule("race", monday, 1, road.stages) } };
}

/** The medal a clear in `ms` earns ("" = slower than bronze). */
export function raceMedal(road: RaceRoadData, ms: number): Medal | "" {
  return ms <= road.goldMs ? "gold" : ms <= road.silverMs ? "silver" : ms <= road.bronzeMs ? "bronze" : "";
}

/** The medals a `medal` clear pays that `paid` lacks, lowest first (a gold pays silver and bronze too if they are still owed). */
export function medalsOwed(medal: Medal | "", paid: readonly string[]): Medal[] {
  const upTo = medal === "" ? -1 : MEDALS.indexOf(medal);
  return MEDALS.filter((m, i) => i <= upTo && !paid.includes(m));
}

// ------------------------------------------------------------------ Odyssey

/** The week's Odyssey: data.odyssey.weeks in turn by the Odyssey's own week count. */
export function odysseyWeek(data: EventsData, monday: number): OdysseyWeekData { return pick(modesOf(data).odyssey.weeks, kindRound(data, monday)); }

/**
 * The deck an Odyssey run deals, as tower indexes: the week's towers the player owns, topped up from Iron's
 * fallbackOrder (then any owned tower) to the deck's size, so a player missing one still gets a full deck.
 */
export function odysseyDeck(balance: BalanceData, ids: readonly string[], owned: readonly boolean[]): number[] {
  const index = (id: string) => balance.towers.findIndex((t) => t.id === id && !t.isBase);
  const out: number[] = [];
  const take = (i: number) => { if (i >= 0 && owned[i] === true && !out.includes(i) && out.length < ids.length) out.push(i); };
  for (const id of ids) take(index(id));
  for (const id of balance.modes.iron.fallbackOrder) take(index(id));
  for (let i = 0; i < balance.towers.length; i++) if (!balance.towers[i].isBase) take(i);
  return out;
}

/** Map `map` (1-based) of the week's Odyssey starting with `lives` Fortress HP (0 = full) at the player's `anchor`. */
export function odysseyMap(balance: BalanceData, monday: number, map: number, lives: number, anchor: number, owned: readonly boolean[]): { level: number; modifiers: LevelModifiers } {
  const d = modesOf(balance.events).odyssey, w = odysseyWeek(balance.events, monday);
  const level = Math.min(w.maps[map - 1] ?? w.maps[0], balance.levels.length);
  const towers = odysseyDeck(balance, w.deck, owned);
  return { level, modifiers: { enemyHpMult: anchorHpMult(balance, level, anchor), event: rule("odyssey", monday, map, d.stages, { towers, lives }) } };
}
